import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API_BASE } from "../lib/api";
const REKISA_LOGO = "https://customer-assets.emergentagent.com/job_growth-workshop/artifacts/5k7dsa9t_Rekisa%20white.png";

export default function PaymentCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying, success, failed
  const [orderData, setOrderData] = useState(null);
  
  const reference = searchParams.get("reference") || searchParams.get("trxref");
  
  useEffect(() => {
    if (reference) {
      verifyPayment();
    } else {
      setStatus("failed");
    }
  }, [reference]);
  
  const verifyPayment = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/payment/verify/${reference}`);
      
      if (response.data.status === "success") {
        setStatus("success");
        setOrderData(response.data.data);
        // Redirect to thank you page after short delay
        setTimeout(() => {
          navigate(`/thank-you?reference=${reference}`);
        }, 2000);
      } else {
        setStatus("failed");
      }
    } catch (error) {
      console.error("Verification error:", error);
      setStatus("failed");
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        <div className="bg-slate-900 px-4 py-2 rounded-lg inline-block mb-12">
          <img src={REKISA_LOGO} alt="Rekisa" className="h-8" />
        </div>
        
        {status === "verifying" && (
          <div data-testid="payment-verifying" className="bg-white rounded-2xl p-8 shadow-lg">
            <Loader2 className="w-16 h-16 text-[#007AFF] mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Verifying Payment
            </h1>
            <p className="text-slate-600">
              Please wait while we confirm your payment...
            </p>
          </div>
        )}
        
        {status === "success" && (
          <div data-testid="payment-success" className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="w-20 h-20 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#10B981]" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Payment Successful!
            </h1>
            <p className="text-slate-600 mb-4">
              Thank you for registering for the Growth Marketing Workshop.
            </p>
            <p className="text-sm text-slate-500">
              Redirecting you to confirmation page...
            </p>
          </div>
        )}
        
        {status === "failed" && (
          <div data-testid="payment-failed" className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Payment Failed
            </h1>
            <p className="text-slate-600 mb-8">
              We couldn't verify your payment. Please try again or contact support.
            </p>
            <div className="flex flex-col gap-4">
              <Button
                data-testid="retry-payment-btn"
                onClick={() => navigate("/#pricing")}
                className="h-12 bg-[#007AFF] text-white font-semibold rounded-full hover:bg-[#0066DD]"
              >
                Try Again
              </Button>
              <Button
                data-testid="go-home-btn"
                variant="outline"
                onClick={() => navigate("/")}
                className="h-12 border-slate-300 text-slate-700 rounded-full hover:bg-slate-50"
              >
                Go Back Home
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
